import { Star, StarHalf } from "lucide-react";
import { cn } from "@/lib/utils";

type StarRatingProps = {
    value: number;
    count?: number;
    size?: "sm" | "md";
    showCount?: boolean;
    className?: string;
};

/**
 * Compact rating display with proper half-star support.
 * Not interactive — for interactive rating we'll add a separate component.
 */
export function StarRating({
    value,
    count,
    size = "sm",
    showCount = true,
    className,
}: StarRatingProps) {
    const clamped = Math.max(0, Math.min(5, value));
    const full = Math.floor(clamped);
    const half = clamped - full >= 0.5;
    const empty = 5 - full - (half ? 1 : 0);

    const sizeClass = size === "sm" ? "h-3 w-3" : "h-4 w-4";

    return (
        <div className={cn("inline-flex items-center gap-0.5", className)}>
            {Array.from({ length: full }).map((_, i) => (
                <Star key={`f-${i}`} className={cn(sizeClass, "fill-accentbrand text-accentbrand")} />
            ))}
            {half ? (
                <span className="relative">
                    <Star className={cn(sizeClass, "text-accentbrand/40")} />
                    <StarHalf
                        className={cn(sizeClass, "absolute inset-0 fill-accentbrand text-accentbrand")}
                    />
                </span>
            ) : null}
            {Array.from({ length: empty }).map((_, i) => (
                <Star key={`e-${i}`} className={cn(sizeClass, "text-accentbrand/30")} />
            ))}
            <span
                className={cn(
                    "ml-1 font-semibold text-foreground",
                    size === "sm" ? "text-xs" : "text-sm",
                )}
            >
                {clamped.toFixed(1)}
            </span>
            {showCount && typeof count === "number" ? (
                <span className={cn("text-muted-foreground", size === "sm" ? "text-xs" : "text-sm")}>
                    ({count.toLocaleString()})
                </span>
            ) : null}
        </div>
    );
}

export default StarRating;
